import { useMemo, useContext } from 'react';
import { Context } from '../../../../../Context';
import { getCompartmentsListConfig } from '../../../config';

/**
 * Custom hook for filtering and sorting compartments based on mode
 * Uses mode configuration to determine filtering behavior
 * @param {Object} apartmentData - Full apartment data object
 * @param {string} mode - Current view mode (info, arrange, hotSpot, etc.)
 * @returns {Object} - Filtered and sorted compartments
 */
export function useCompartmentFiltering(apartmentData, mode) {
	const { compartmentsDictionary } = useContext(Context);
	const listConfig = getCompartmentsListConfig(mode);

	const filteredCompartments = useMemo(() => {
		if (!apartmentData?.compartments) {
			return {
				indoorCompartments: [],
				outdoorCompartments: [],
				allCompartments: [],
			};
		}

		const compartments = apartmentData.compartments;

		// Get outdoor compartment IDs from dictionary
		const outdoorCompartmentIds = compartmentsDictionary
			?.filter((room) => room.group === 'OutdoorSpace')
			.map((room) => room.id) || [];
		// Split into indoor and outdoor
		const unsortedIndoorCompartments = compartments.filter(
			(room) => !outdoorCompartmentIds.includes(room.name_cad)
		);
		// Sort indoor compartments by number
		const indoorCompartments = unsortedIndoorCompartments.sort((a, b) => {
			return parseInt(a.number, 10) - parseInt(b.number, 10);
		});

		const outdoorCompartments = compartments.filter((room) =>
			outdoorCompartmentIds.includes(room.name_cad)
		);

		// Filter based on mode configuration
		let visibleIndoorCompartments = indoorCompartments;
		let visibleOutdoorCompartments = outdoorCompartments;

		// Apply filtering based on mode config
		if (!listConfig.showIndoorCompartments) {
			visibleIndoorCompartments = [];
		}
		if (!listConfig.showOutdoorCompartments) {
			visibleOutdoorCompartments = [];
		}

		// Special filtering for modes with filterBy property
		if (listConfig.filterBy === 'hasHotSpot') {
			// Only show compartments that have hotspots
			const currentTier = apartmentData.compartments[0]?.ref_tier;
			const apartmentTiersData = apartmentData.tiers?.find(
				(tier) => tier.ref_tier === currentTier
			);
			const apartmentCameras = apartmentTiersData?.cameras || [];
			const apartmentHotSpots = apartmentCameras.filter(
				(camera) => camera.name !== 'top_3D' && camera.name !== 'axonometric'
			);
			const compartmentsWithHotSpots = apartmentHotSpots.map(
				(hotSpot) => hotSpot.name.split('_')[1]
			);

			// Get minimum number for each compartment name
			const minNumberByName = {};
			indoorCompartments.forEach((c) => {
				if (
					compartmentsWithHotSpots.includes(c.name_cad) &&
					(minNumberByName[c.name_cad] === undefined ||
						c.number < minNumberByName[c.name_cad])
				) {
					minNumberByName[c.name_cad] = c.number;
				}
			});

			// Filter to only show first occurrence of each compartment with hotspot
			visibleIndoorCompartments = indoorCompartments.filter(
				(c) =>
					compartmentsWithHotSpots.includes(c.name_cad) &&
					c.number === minNumberByName[c.name_cad]
			);

			// Typically don't show outdoor compartments when filtering by hotspots
			visibleOutdoorCompartments = [];
		}

        return {
			indoorCompartments: visibleIndoorCompartments,
			outdoorCompartments: visibleOutdoorCompartments,
			allCompartments: [...visibleIndoorCompartments, ...visibleOutdoorCompartments],
			listConfig, // Return config for convenience
		};
	}, [apartmentData, mode, compartmentsDictionary, listConfig]);

	return filteredCompartments;
}
